import React from "react";

// SourdoughArt — the Social community's custom brand art: a bread mascot,
// a hand-built line-icon set (tints to the firm's brand color via currentColor),
// and a spot illustration. Replaces decorative emoji with an authentic, ownable
// visual language. Scoped to the Social tab for now.

const DOUGH = "#EEC988";       // warm crust
const DOUGH_LT = "#F7E2B3";    // flour
const DOUGH_MID = "#D6A961";   // score
const DOUGH_DK = "#D3A05C";    // underside shelf (depth)
const SCORE = "#B87C41";       // ear-score fill
const SCORE_HL = "#F3DEAA";    // raised score lip
const INK = "#4A3F2E";         // friendly warm outline
const FACE = "#3B372F";
const CHEEK = "#E9A9B4";
const LAV = "#8B7CC8";
const GREEN = "var(--growth)";   // sprout / seedling / celebrate spark — growth-green, stays green when the brand re-tones

export type Mood = "happy" | "think" | "celebrate";

/* ── Motion (respects prefers-reduced-motion) ── */
const KEYFRAMES = `
@keyframes mp-bob { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-3.5px)} }
@keyframes mp-pop { 0%{transform:scale(1)} 28%{transform:scale(1.13) rotate(-4deg)} 52%{transform:scale(.96) rotate(2deg)} 76%{transform:scale(1.03)} 100%{transform:scale(1)} }
@keyframes mp-wiggle { 0%,100%{transform:rotate(0)} 25%{transform:rotate(-6deg)} 75%{transform:rotate(6deg)} }
.mp-mascot{ display:inline-block; transform-origin:center bottom; }
.mp-mascot.mp-idle{ animation:mp-bob 3.4s ease-in-out infinite; }
.mp-mascot.mp-pop{ animation:mp-pop .62s ease-out; }
.mp-mascot.mp-hover:hover{ animation:mp-wiggle .5s ease-in-out; }
@media (prefers-reduced-motion: reduce){ .mp-mascot{ animation:none !important; } }
`;
function ensureMascotStyles() {
  // Inject the keyframes once into <head> (idempotent by id — survives
  // mount/unmount cycles, unlike a rendered <style> or a module flag).
  if (typeof document === "undefined" || document.getElementById("mp-mascot-styles")) return;
  const el = document.createElement("style");
  el.id = "mp-mascot-styles";
  el.textContent = KEYFRAMES;
  document.head.appendChild(el);
}

/* ── Shared face — one family, three moods ── */
function Sparkles({ cx, cy, s }: { cx: number; cy: number; s: number }) {
  const star = (x: number, y: number, r: number, c: string) => (
    <path d={`M${x} ${y - r} L${x + r * 0.3} ${y - r * 0.3} L${x + r} ${y} L${x + r * 0.3} ${y + r * 0.3} L${x} ${y + r} L${x - r * 0.3} ${y + r * 0.3} L${x - r} ${y} L${x - r * 0.3} ${y - r * 0.3} Z`} fill={c} />
  );
  return <g>{star(cx - 17 * s, cy - 7 * s, 2.4 * s, GREEN)}{star(cx + 17 * s, cy - 10 * s, 2 * s, LAV)}{star(cx + 14 * s, cy + 7 * s, 1.6 * s, DOUGH_MID)}</g>;
}

function Face({ cx, cy, mood = "happy", s = 1 }: { cx: number; cy: number; mood?: Mood; s?: number }) {
  const dx = 6 * s, r = 2.7 * s;
  const eyeY = mood === "think" ? cy - 1.5 * s : cy;
  return (
    <g>
      <circle cx={cx - 10.5 * s} cy={cy + 2 * s} r={3 * s} fill={CHEEK} opacity="0.7" />
      <circle cx={cx + 10.5 * s} cy={cy + 2 * s} r={3 * s} fill={CHEEK} opacity="0.7" />
      {mood === "celebrate" ? (
        <>
          <path d={`M${cx - dx - 2.4 * s} ${eyeY + 0.5 * s} q${2.4 * s} ${-3.4 * s} ${4.8 * s} 0`} stroke={FACE} strokeWidth={2.3 * s} fill="none" strokeLinecap="round" />
          <path d={`M${cx + dx - 2.4 * s} ${eyeY + 0.5 * s} q${2.4 * s} ${-3.4 * s} ${4.8 * s} 0`} stroke={FACE} strokeWidth={2.3 * s} fill="none" strokeLinecap="round" />
        </>
      ) : (
        <>
          <circle cx={cx - dx} cy={eyeY} r={r} fill={FACE} />
          <circle cx={cx + dx} cy={eyeY} r={r} fill={FACE} />
          <circle cx={cx - dx + 1 * s} cy={eyeY - 0.8 * s} r={0.9 * s} fill="#fff" />
          <circle cx={cx + dx + 1 * s} cy={eyeY - 0.8 * s} r={0.9 * s} fill="#fff" />
        </>
      )}
      <path d={`M${cx - 6 * s} ${cy + 4.4 * s} Q${cx} ${cy + (mood === "think" ? 6.5 : 9.5) * s} ${cx + 6 * s} ${cy + 4.4 * s}`}
        stroke={FACE} strokeWidth={2.5 * s} strokeLinecap="round" fill={mood === "celebrate" ? FACE : "none"} strokeLinejoin="round" />
      {mood === "celebrate" && <Sparkles cx={cx} cy={cy} s={s} />}
      {mood === "think" && (
        <g stroke="none" fill={FACE} opacity="0.5">
          <circle cx={cx + 12 * s} cy={cy - 6 * s} r={1 * s} />
          <circle cx={cx + 15.5 * s} cy={cy - 9.5 * s} r={1.4 * s} />
          <circle cx={cx + 19.5 * s} cy={cy - 13.5 * s} r={1.8 * s} />
        </g>
      )}
    </g>
  );
}

/* wheat-sheaf score mark (the botanical scoring) */
function Wheat({ x, y, scale = 1 }: { x: number; y: number; scale?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <path d="M0 0 C1 -7 3 -13 6 -19 C7 -21 8 -23 9 -25" stroke={SCORE} strokeWidth="2" strokeLinecap="round" fill="none" />
      <g fill={SCORE} stroke="none">
        <path d="M2 -5 C-2 -6 -4 -10 -2 -13 C1 -11 3 -8 2 -5Z" />
        <path d="M3 -6 C7 -8 9 -12 7 -15 C4 -13 2 -9 3 -6Z" />
        <path d="M4.5 -12 C0.5 -13 -1.5 -17 0.5 -20 C3.5 -18 5.5 -15 4.5 -12Z" />
        <path d="M5.5 -13 C9.5 -15 11.5 -19 9.5 -22 C6.5 -20 4.5 -16 5.5 -13Z" />
        <path d="M9 -25 C6 -26 6 -30 8 -31 C10.5 -29 10 -26 9 -25Z" />
      </g>
    </g>
  );
}

/* ── Mascot: a round sourdough boule, the face of the Social community ── */
export function Mascot({ size = 72, mood = "happy", animate = "idle", title = "Sourdough" }:
  { size?: number; mood?: Mood; animate?: "idle" | "pop" | "none"; title?: string }) {
  React.useEffect(() => { ensureMascotStyles(); }, []);
  const cls = `mp-mascot mp-hover ${animate === "idle" ? "mp-idle" : animate === "pop" ? "mp-pop" : ""}`.trim();
  return (
    <span className={cls} style={{ lineHeight: 0 }}>
      <svg width={size} height={size * (78 / 72)} viewBox="0 0 72 78" fill="none" role="img" aria-label={title} style={{ display: "block", overflow: "visible" }}>
        {/* underside shelf (depth) */}
        <ellipse cx="36" cy="52" rx="28" ry="21" fill={DOUGH_DK} />
        {/* body — round boule */}
        <ellipse cx="36" cy="47" rx="28" ry="24" fill={DOUGH} stroke={INK} strokeWidth="3" />
        {/* top-left crust highlight */}
        <path d="M17 39 C20 30 29 26 39 28 C33 35 25 39 18 42 Z" fill={SCORE_HL} opacity="0.4" />
        {/* wheat + ear score (sourdough signature, up top so the face stays clear) */}
        <Wheat x={19} y={41} scale={0.7} />
        <path d="M30 32 Q39 27 50 31" stroke={SCORE_HL} strokeWidth="3" fill="none" strokeLinecap="round" />
        <path d="M31 34 Q39 29 49 33" stroke={SCORE} strokeWidth="4.2" fill="none" strokeLinecap="round" />
        {/* shine + flour dusting */}
        <path d="M17 44 C15 49 15 54 17 58" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" opacity="0.38" />
        <circle cx="52" cy="43" r="1.3" fill={DOUGH_LT} /><circle cx="48" cy="39" r="1" fill={DOUGH_LT} />
        <Face cx={36} cy={49} mood={mood} s={1.05} />
      </svg>
    </span>
  );
}

/* ── Spot illustration: a seedling for "growth" ── */
export function Seedling({ size = 96 }: { size?: number }) {
  return (
    <svg width={size} height={size * 0.8} viewBox="0 0 120 96" fill="none" role="img" aria-label="A seedling" style={{ display: "block" }}>
      <path d="M18 78 Q60 62 102 78 Q102 86 60 86 Q18 86 18 78 Z" fill="#E9DCC4" />
      <path d="M18 78 Q60 62 102 78" stroke={INK} strokeWidth="2.4" strokeLinecap="round" opacity="0.55" />
      <path d="M60 78 C59 66 60 56 60 46" stroke={GREEN} strokeWidth="3.4" strokeLinecap="round" />
      <path d="M60 58 C50 58 43 50 46 41 C56 41 62 49 60 58 Z" fill={GREEN} />
      <path d="M60 52 C71 52 78 43 75 34 C64 34 58 43 60 52 Z" fill={GREEN} opacity="0.92" />
      <path d="M60 58 C55 55 51 50 48 45 M60 52 C64 49 68 45 71 41" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" opacity="0.6" />
      <circle cx="96" cy="28" r="6" fill={DOUGH} />
      <path d="M96 16 v-5 M96 45 v5 M108 28 h5 M84 28 h-5 M105 19 l3-3 M87 37 l-3 3 M105 37 l3 3 M87 19 l-3-3" stroke={DOUGH} strokeWidth="2.2" strokeLinecap="round" opacity="0.8" />
    </svg>
  );
}

/* ── Icon set: monoline, inherits color via currentColor ── */
type IconName =
  | "community" | "link" | "leaf" | "asleep" | "compass"
  | "feed" | "chat" | "activity" | "flame" | "pencil" | "spark" | "globe"
  | "dashboard" | "wallet" | "target" | "doc" | "calendar" | "check" | "checkCircle"
  | "alert" | "info" | "star" | "bell" | "plus" | "arrowRight" | "chevronLeft" | "clock" | "shield"
  | "home" | "search" | "gear" | "mail" | "lock" | "download" | "folder" | "heart"
  | "chart" | "trash" | "eye" | "filter" | "rocket" | "handshake" | "megaphone" | "book"
  | "close" | "refresh" | "upload" | "phone" | "building" | "tag" | "pin" | "save"
  | "help" | "user";

const PATHS: Record<IconName, React.ReactNode> = {
  community: <><circle cx="9" cy="8" r="3.2" /><path d="M3.5 19c0-3.2 2.5-5.5 5.5-5.5s5.5 2.3 5.5 5.5" /><circle cx="17.5" cy="9" r="2.4" /><path d="M15.5 14.2c2.7 0 5 2 5 4.8" /></>,
  link: <><path d="M9.5 14.5l5-5" /><path d="M8 12l-1.8 1.8a3.5 3.5 0 0 0 5 5L13 17" /><path d="M16 12l1.8-1.8a3.5 3.5 0 0 0-5-5L11 7" /></>,
  leaf: <><path d="M5 19c0-8 6.5-13.5 14.5-14C20 13 14.5 20 5 19z" /><path d="M5.5 18.5c3-4 6-7 9.5-9.5" /></>,
  asleep: <><path d="M20 13.5A7.5 7.5 0 1 1 10.5 4a6 6 0 0 0 9.5 9.5z" /><path d="M15 4h4l-4 4h4" strokeWidth="1.5" /></>,
  compass: <><circle cx="12" cy="12" r="9" /><path d="M15.5 8.5l-2.2 5.3-5.3 2.2 2.2-5.3z" fill="currentColor" stroke="none" /></>,
  feed: <><path d="M5 10v4a1 1 0 0 0 1 1h2l4.5 3.2V5.8L8 9H6a1 1 0 0 0-1 1z" /><path d="M16.5 8.5a5 5 0 0 1 0 7" /></>,
  chat: <path d="M20 11.5a7.5 7.5 0 0 1-10.9 6.7L4.5 19.5l1.4-4A7.5 7.5 0 1 1 20 11.5z" />,
  activity: <path d="M3 12h4l2.5-7 4 14L16 12h5" />,
  flame: <path d="M12.5 3c2.8 3 4.3 5.6 4.3 8.6a4.8 4.8 0 0 1-9.6 0c0-1.4.5-2.6 1.4-3.6.1 1.4 1 2.2 1.8 2.2 1.1 0 1.4-1.1 1-2.6-.4-2 .3-3.4 1.1-4.6z" />,
  pencil: <><path d="M4 20l1-4.2L16.2 4.6a1.6 1.6 0 0 1 2.3 0l1 1a1.6 1.6 0 0 1 0 2.3L8.2 19z" /><path d="M14.5 6.5l3 3" /></>,
  spark: <path d="M12 3.5l1.9 5.4 5.4 1.9-5.4 1.9L12 18.1l-1.9-5.4L4.7 10.8l5.4-1.9z" fill="currentColor" stroke="none" />,
  globe: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c3.2 3 3.2 15 0 18M12 3c-3.2 3-3.2 15 0 18" /></>,
  dashboard: <><rect x="3.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="3.5" y="13.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="13.5" width="7" height="7" rx="1.5" /></>,
  wallet: <><rect x="3" y="6" width="18" height="13" rx="2.5" /><path d="M3 9.5h18" /><circle cx="16.5" cy="13.5" r="1.2" fill="currentColor" stroke="none" /></>,
  target: <><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.5" /><circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" /></>,
  doc: <><path d="M6 3h7l5 5v12.5a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-17a.5.5 0 0 1 .5-.5z" /><path d="M13 3v5h5" /><path d="M8.5 13h7M8.5 16.5h7" /></>,
  calendar: <><rect x="3.5" y="5" width="17" height="16" rx="2.5" /><path d="M3.5 9.5h17M8 3v4M16 3v4" /></>,
  check: <path d="M4.5 12.5l4.5 4.5 10-11" />,
  checkCircle: <><circle cx="12" cy="12" r="9" /><path d="M8 12.2l2.8 2.8L16 9" /></>,
  alert: <><path d="M12 3.6l9.3 16.2a.5.5 0 0 1-.43.75H3.13a.5.5 0 0 1-.43-.75z" /><path d="M12 10v4.2" /><circle cx="12" cy="17.4" r="0.6" fill="currentColor" stroke="none" /></>,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11.2v5" /><circle cx="12" cy="7.8" r="0.7" fill="currentColor" stroke="none" /></>,
  star: <path d="M12 3.5l2.6 5.6 6.1.7-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6-4.5-4.2 6.1-.7z" />,
  bell: <><path d="M6 10a6 6 0 0 1 12 0c0 5 1.5 6 1.5 6H4.5S6 15 6 10z" /><path d="M10 19a2 2 0 0 0 4 0" /></>,
  plus: <path d="M12 5v14M5 12h14" />,
  arrowRight: <path d="M4 12h15M13 6l6 6-6 6" />,
  // Bare chevron (no shaft) — the native iOS/Android back-nav mark, distinct
  // from arrowRight's full arrow so a back button doesn't look like a "go
  // forward" arrow pointed the wrong way.
  chevronLeft: <path d="M15 5l-7 7 7 7" strokeWidth="2.4" />,
  clock: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7v5.2l3.2 2" /></>,
  shield: <path d="M12 3l7 3v5c0 5-3.2 8.5-7 10-3.8-1.5-7-5-7-10V6z" />,
  home: <><path d="M4 11.5L12 4l8 7.5" /><path d="M6 10.5V20h12v-9.5" /></>,
  search: <><circle cx="10.5" cy="10.5" r="6.5" /><path d="M20 20l-4.8-4.8" /></>,
  gear: <><circle cx="12" cy="12" r="3.2" /><path d="M12 2.5v3M12 18.5v3M21.5 12h-3M5.5 12h-3M18.7 5.3l-2.1 2.1M7.4 16.6l-2.1 2.1M18.7 18.7l-2.1-2.1M7.4 7.4L5.3 5.3" /></>,
  mail: <><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M4 7.5l8 6 8-6" /></>,
  lock: <><rect x="5" y="10.5" width="14" height="10" rx="2.5" /><path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" /></>,
  download: <><path d="M12 4v11M7.5 10.5L12 15l4.5-4.5" /><path d="M5 19.5h14" /></>,
  folder: <path d="M3.5 6.5A1.5 1.5 0 0 1 5 5h4l2 2.5h6.5A1.5 1.5 0 0 1 19 9v9.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 3 18.5z" />,
  heart: <path d="M12 20S3.5 14.5 3.5 8.8A4.3 4.3 0 0 1 12 6.8a4.3 4.3 0 0 1 8.5 2c0 5.7-8.5 11.2-8.5 11.2z" />,
  chart: <><path d="M4 20V4" /><path d="M4 20h16" /><path d="M8 16v-4M12.5 16V8M17 16v-6" /></>,
  trash: <><path d="M4.5 7h15M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6.5 7l1 12.5a1 1 0 0 0 1 .9h7a1 1 0 0 0 1-.9L18 7" /></>,
  eye: <><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" /><circle cx="12" cy="12" r="3" /></>,
  filter: <path d="M4 5h16l-6.2 7.4V19l-3.6-2v-4.6z" />,
  rocket: <><path d="M12 3c3.5 2 5 5.5 5 9l-3 3H10L7 12c0-3.5 1.5-7 5-9z" /><circle cx="12" cy="9.5" r="1.6" /><path d="M10 15c-1.5.5-2.5 2-2.5 4 2 0 3.5-1 4-2.5M14 15c1.5.5 2.5 2 2.5 4-2 0-3.5-1-4-2.5" /></>,
  handshake: <path d="M3 8l4-2 5 3 5-3 4 2v6l-4 2-3-2.5M12 9v5.5a2 2 0 0 1-2 2l-2-1.5-2 1.5-3-2V8" />,
  megaphone: <><path d="M4 10v4a1 1 0 0 0 1 1h2l6 4V5L7 9H5a1 1 0 0 0-1 1z" /><path d="M17 8.5a5 5 0 0 1 0 7" /></>,
  book: <><path d="M5 4.5A1.5 1.5 0 0 1 6.5 3H19v15H6.5A1.5 1.5 0 0 0 5 19.5z" /><path d="M5 19.5A1.5 1.5 0 0 1 6.5 21H19" /></>,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  refresh: <><path d="M20 11a8 8 0 0 0-14.3-4.3M4 4.5V9h4.5" /><path d="M4 13a8 8 0 0 0 14.3 4.3M20 19.5V15h-4.5" /></>,
  upload: <><path d="M12 20V9M7.5 13.5L12 9l4.5 4.5" /><path d="M5 4.5h14" /></>,
  phone: <path d="M6 3h3l1.5 4.5-2 1.5a11 11 0 0 0 5 5l1.5-2 4.5 1.5V21a1 1 0 0 1-1.1 1A17 17 0 0 1 5 4.1 1 1 0 0 1 6 3z" />,
  building: <><rect x="5" y="3.5" width="14" height="17" rx="1.5" /><path d="M9 7h2M13 7h2M9 11h2M13 11h2M9.5 20.5v-3h5v3" /></>,
  tag: <><path d="M3.5 11.5l8-8H20v8.5l-8 8z" /><circle cx="16" cy="8" r="1.2" fill="currentColor" stroke="none" /></>,
  pin: <><path d="M12 21s6-5.3 6-10a6 6 0 0 0-12 0c0 4.7 6 10 6 10z" /><circle cx="12" cy="11" r="2.2" /></>,
  save: <><path d="M5 4.5h11l3 3V19a.5.5 0 0 1-.5.5H5a.5.5 0 0 1-.5-.5V5a.5.5 0 0 1 .5-.5z" /><path d="M8 4.5v4h6v-4M8 19v-5h8v5" /></>,
  help: <><circle cx="12" cy="12" r="9" /><path d="M9.4 9.4a2.7 2.7 0 0 1 5.2.9c0 1.8-2.6 2.1-2.6 3.9" /><circle cx="12" cy="17.4" r="0.7" fill="currentColor" stroke="none" /></>,
  user: <><circle cx="12" cy="8.5" r="3.7" /><path d="M4.8 20c0-3.8 3.2-6.3 7.2-6.3s7.2 2.5 7.2 6.3" /></>,
};

export function Icon({ name, size = 18, strokeWidth = 1.8, style, ...rest }:
  { name: IconName; size?: number; strokeWidth?: number; style?: React.CSSProperties; [k: string]: any }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      {...rest}
      style={{ display: "inline-block", verticalAlign: "-0.15em", flexShrink: 0, ...(style as React.CSSProperties) }}>
      {PATHS[name]}
    </svg>
  );
}

export type { IconName };
